/**
 * Summary Table Component
 *
 * @package
 */

import { useState, useEffect, Fragment } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import ProductInfoModal from './ProductInfoModal';
import { decodeHtmlEntities } from '../helpers/decodeHtmlEntities';
import { getImageAltText } from '../helpers/getImageAltText';
import { formatPrice, getProductLine } from '../helpers/priceHelpers';
import { cachedApiFetch } from '../helpers/useApiCache';
import InstallationBadge from './InstallationBadge';
import QuantitySelector from './QuantitySelector';

const SummaryTable = ( {
	config,
	selectedBaseModel,
	selectedProducts,
	selectedBundle,
	vatMode = 'including',
	quantities = {},
	setProductQuantity,
	workshopHourlyRate = 0,
	taxRate = 0,
} ) => {
	const [ baseModelData, setBaseModelData ] = useState( null );
	const [ bundleData, setBundleData ] = useState( null );
	const [ productsData, setProductsData ] = useState( [] );
	const [ baseModelLoading, setBaseModelLoading ] = useState( true );
	const [ productsLoading, setProductsLoading ] = useState( false );
	const loading = baseModelLoading || productsLoading;
	const [ infoModalProductId, setInfoModalProductId ] = useState( null );
	const [ infoModalIsOpen, setInfoModalIsOpen ] = useState( false );
	const [ infoModalIsSelected, setInfoModalIsSelected ] = useState( false );

	useEffect( () => {
		if ( ! selectedBundle ) {
			setBundleData( null );
			return;
		}

		// `ignore` drops a superseded response so a slow earlier request can't
		// overwrite a newer selection.
		let ignore = false;

		cachedApiFetch( `/wp/v2/bundles/${ selectedBundle }?_embed` )
			.then( data => {
				if ( ignore ) {
					return;
				}
				// Extract featured image URL from embedded media.
				let featuredImageUrl = null;
				if ( data._embedded && data._embedded[ 'wp:featuredmedia' ] && data._embedded[ 'wp:featuredmedia' ][ 0 ] ) {
					featuredImageUrl = data._embedded[ 'wp:featuredmedia' ][ 0 ].source_url;
				} else if ( data.featured_media ) {
					// Fallback: fetch media separately if not embedded.
					cachedApiFetch( `/wp/v2/media/${ data.featured_media }` )
						.then( media => {
							if ( ignore ) {
								return;
							}
							setBundleData( {
								...data,
								featured_media_url: media.source_url,
							} );
						} )
						.catch( () => {
							if ( ! ignore ) {
								setBundleData( data );
							}
						} );
					return;
				}
				setBundleData( {
					...data,
					featured_media_url: featuredImageUrl,
				} );
			} )
			.catch( () => {
				if ( ! ignore ) {
					setBundleData( null );
				}
			} );

		return () => {
			ignore = true;
		};
	}, [ selectedBundle ] );

	useEffect( () => {
		if ( ! selectedBaseModel ) {
			setBaseModelData( null );
			setBaseModelLoading( false );
			return;
		}

		let ignore = false;

		setBaseModelLoading( true );
		cachedApiFetch( `/wp/v2/qcom_product/${ selectedBaseModel }?_embed` )
			.then( data => {
				if ( ignore ) {
					return;
				}
				// Extract featured image URL from embedded media.
				let featuredImageUrl = null;
				if ( data._embedded && data._embedded[ 'wp:featuredmedia' ] && data._embedded[ 'wp:featuredmedia' ][ 0 ] ) {
					featuredImageUrl = data._embedded[ 'wp:featuredmedia' ][ 0 ].source_url;
				} else if ( data.featured_media ) {
					// Fallback: fetch media separately if not embedded.
					cachedApiFetch( `/wp/v2/media/${ data.featured_media }` )
						.then( media => {
							if ( ignore ) {
								return;
							}
							setBaseModelData( {
								...data,
								featured_media_url: media.source_url,
							} );
						} )
						.catch( () => {
							if ( ! ignore ) {
								setBaseModelData( data );
							}
						} );
					return;
				}
				setBaseModelData( {
					...data,
					featured_media_url: featuredImageUrl,
				} );
			} )
			.catch( () => {
				if ( ! ignore ) {
					setBaseModelData( null );
				}
			} )
			.finally( () => {
				if ( ! ignore ) {
					setBaseModelLoading( false );
				}
			} );

		return () => {
			ignore = true;
		};
	}, [ selectedBaseModel ] );

	useEffect( () => {
		if ( selectedProducts.length === 0 ) {
			setProductsData( [] );
			setProductsLoading( false );
			return;
		}

		let ignore = false;

		setProductsLoading( true );
		const productPromises = selectedProducts.map( productId =>
			cachedApiFetch( `/wp/v2/qcom_product/${ productId }?_embed` )
				.then( data => {
					let featuredImageUrl = null;
					if ( data._embedded && data._embedded[ 'wp:featuredmedia' ] && data._embedded[ 'wp:featuredmedia' ][ 0 ] ) {
						featuredImageUrl = data._embedded[ 'wp:featuredmedia' ][ 0 ].source_url;
					} else if ( data.featured_media ) {
						// Fallback: fetch media separately if not embedded.
						return cachedApiFetch( `/wp/v2/media/${ data.featured_media }` )
							.then( media => ( {
								id: productId,
								data: {
									...data,
									featured_media_url: media.source_url,
								},
							} ) )
							.catch( () => ( { id: productId, data } ) );
					}
					return {
						id: productId,
						data: {
							...data,
							featured_media_url: featuredImageUrl,
						},
					};
				} )
				.catch( () => ( { id: productId, data: null } ) )
		);

		Promise.all( productPromises )
			.then( results => {
				if ( ! ignore ) {
					setProductsData( results );
				}
			} )
			.finally( () => {
				if ( ! ignore ) {
					setProductsLoading( false );
				}
			} );

		return () => {
			ignore = true;
		};
	}, [ selectedProducts ] );

	// Priority: REST API field > meta fields > ACF.
	// TODO: This can be tidied up when we have a proper SKU field in the REST API.
	const getSku = productData => {
		if ( ! productData ) {
			return null;
		}

		return productData.sku ||
			productData.meta?._qcom_sku?.[ 0 ] ||
			productData.meta?.sku?.[ 0 ] ||
			productData.acf?.sku ||
			null;
	};

	const getImageUrl = productData => {
		if ( ! productData ) {
			return null;
		}

		return productData.featured_media_url || null;
	};

	const findProductGroup = productId => {
		if ( ! config?.productGroups ) {
			return null;
		}

		// Convert productId to number for consistent comparison.
		const productIdNum = typeof productId === 'string' ? parseInt( productId, 10 ) : productId;

		for ( const group of config.productGroups ) {
			if ( group.products && group.products.length > 0 ) {
				const hasProduct = group.products.some(
					id => ( typeof id === 'string' ? parseInt( id, 10 ) : id ) === productIdNum
				);
				if ( hasProduct ) {
					return {
						groupName: group.group_name || __( 'Product Group', 'at-vehicle-configurator' ),
						subGroupName: null,
					};
				}
			}

			if ( group.sub_groups && group.sub_groups.length > 0 ) {
				for ( const subGroup of group.sub_groups ) {
					if ( subGroup.products && subGroup.products.length > 0 ) {
						const hasProduct = subGroup.products.some(
							id => ( typeof id === 'string' ? parseInt( id, 10 ) : id ) === productIdNum
						);
						if ( hasProduct ) {
							return {
								groupName: group.group_name || __( 'Product Group', 'at-vehicle-configurator' ),
								subGroupName: subGroup.sub_group_name || __( 'Sub-group', 'at-vehicle-configurator' ),
							};
						}
					}
				}
			}
		}

		return null;
	};

	const groupedProducts = () => {
		if ( ! productsData.length || ! config?.productGroups ) {
			return [];
		}

		const groups = {};

		productsData.forEach( ( { id, data } ) => {
			if ( ! data ) {
				return;
			}

			const groupInfo = findProductGroup( id );
			if ( ! groupInfo ) {
				// Product not found in any group, add to "Other" group.
				if ( ! groups._other ) {
					groups._other = {
						groupName: __( 'Other', 'at-vehicle-configurator' ),
						subGroups: {},
						directProducts: [],
					};
				}
				groups._other.directProducts.push( { id, data } );
				return;
			}

			const groupKey = groupInfo.groupName;
			if ( ! groups[ groupKey ] ) {
				groups[ groupKey ] = {
					groupName: groupInfo.groupName,
					subGroups: {},
					directProducts: [],
				};
			}

			if ( groupInfo.subGroupName ) {
				const subGroupKey = groupInfo.subGroupName;
				if ( ! groups[ groupKey ].subGroups[ subGroupKey ] ) {
					groups[ groupKey ].subGroups[ subGroupKey ] = [];
				}
				groups[ groupKey ].subGroups[ subGroupKey ].push( { id, data } );
			} else {
				groups[ groupKey ].directProducts.push( { id, data } );
			}
		} );

		return Object.values( groups );
	};

	const getGroupCount = group => {
		let count = group.directProducts.length;
		Object.values( group.subGroups ).forEach( subGroupProducts => {
			count += subGroupProducts.length;
		} );
		return count;
	};

	const handleInfoClick = ( productId, isSelected ) => {
		setInfoModalProductId( productId );
		setInfoModalIsSelected( isSelected );
		setInfoModalIsOpen( true );
	};

	if ( loading && ( ! baseModelData && productsData.length === 0 ) ) {
		const SummaryItemSkeleton = ( { hasImage = true } ) => (
			<div className="configurator-summary-item">
				{ hasImage && (
					<span
						className="configurator-skeleton configurator-summary-item-image-skeleton"
						aria-hidden="true"
					/>
				) }
				<span className="configurator-skeleton configurator-skeleton--line configurator-summary-item-title-skeleton" style={ { width: hasImage ? '100px' : '140px', height: '16px', display: 'block', flex: 1 } } aria-hidden="true" />
			</div>
		);

		return (
			<div id="configurator-summary" className="configurator-summary">
				<h2>{ __( 'Configuration Summary', 'at-vehicle-configurator' ) }</h2>
				<div className="configurator-summary-loading" role="status" aria-live="polite">
					<span className="screen-reader-text">{ __( 'Loading configuration summary…', 'at-vehicle-configurator' ) }</span>
					<div className="configurator-summary-table-wrapper">
						<table className="configurator-summary-table">
							<thead>
								<tr>
									<th>{ __( 'Price Specification', 'at-vehicle-configurator' ) }</th>
									<th>{ __( 'Description', 'at-vehicle-configurator' ) }</th>
									<th>{ __( 'Price', 'at-vehicle-configurator' ) }</th>
								</tr>
							</thead>
							<tbody>
								<tr className="configurator-summary-bundle-row">
									<td><SummaryItemSkeleton hasImage /></td>
									<td><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '120px', height: '14px', display: 'block' } } aria-hidden="true" /></td>
									<td><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '56px', height: '14px', display: 'block', marginLeft: 'auto' } } aria-hidden="true" /></td>
								</tr>
								<tr>
									<td><SummaryItemSkeleton hasImage /></td>
									<td><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '80px', height: '14px', display: 'block' } } aria-hidden="true" /></td>
									<td><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '56px', height: '14px', display: 'block', marginLeft: 'auto' } } aria-hidden="true" /></td>
								</tr>
								<tr className="configurator-summary-group-header">
									<td colSpan="3">
										<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '140px', height: '14px', display: 'block' } } aria-hidden="true" />
									</td>
								</tr>
								<tr>
									<td><SummaryItemSkeleton hasImage /></td>
									<td><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '60px', height: '14px', display: 'block' } } aria-hidden="true" /></td>
									<td><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '56px', height: '14px', display: 'block', marginLeft: 'auto' } } aria-hidden="true" /></td>
								</tr>
								<tr>
									<td><SummaryItemSkeleton hasImage /></td>
									<td><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '60px', height: '14px', display: 'block' } } aria-hidden="true" /></td>
									<td><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '56px', height: '14px', display: 'block', marginLeft: 'auto' } } aria-hidden="true" /></td>
								</tr>
								<tr className="configurator-summary-row-subtotal">
									<td colSpan="2"><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '80px', height: '16px', display: 'block' } } aria-hidden="true" /></td>
									<td><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '56px', height: '16px', display: 'block', marginLeft: 'auto' } } aria-hidden="true" /></td>
								</tr>
								<tr className="configurator-summary-row-total">
									<td colSpan="2"><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '100px', height: '18px', display: 'block' } } aria-hidden="true" /></td>
									<td><span className="configurator-skeleton configurator-skeleton--line" style={ { width: '64px', height: '18px', display: 'block', marginLeft: 'auto' } } aria-hidden="true" /></td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		);
	}

	const colCount = 3;

	// Installation is priced into the line rather than totalled separately, so every
	// row shares one breakdown: price × quantity, plus installation × quantity.
	const getLine = ( data, productId ) => getProductLine( data || {}, {
		vatMode,
		quantity: productId ? quantities[ productId ] : 1,
		hourlyRate: workshopHourlyRate,
		taxRate,
	} );

	// Renders a row's price cell with the spanner marker when part of it is installation.
	const renderPriceCell = line => (
		<td>
			<span className="configurator-summary-item-price">
				{ formatPrice( line.total ) }
				<InstallationBadge hours={ line.installationHours } />
			</span>
		</td>
	);

	// The summary is where a customer reviews the build, so quantities are editable
	// here as well as on the cards rather than sending them back up the page.
	const renderQuantity = ( data, productId ) => {
		if ( ! data?.allow_quantity || ! setProductQuantity ) {
			return null;
		}

		return (
			<QuantitySelector
				quantity={ quantities[ productId ] || 1 }
				onChange={ units => setProductQuantity( productId, units ) }
				productTitle={ decodeHtmlEntities( data.title?.rendered ) || __( 'Product', 'at-vehicle-configurator' ) }
			/>
		);
	};

	return (
		<div id="configurator-summary" className="configurator-summary">
			<div className="configurator-summary-table-wrapper">
				<table className="configurator-summary-table">
					<thead>
						<tr>
							<th>{ __( 'Price Specification', 'at-vehicle-configurator' ) }</th>
							<th>{ __( 'Description', 'at-vehicle-configurator' ) }</th>
							<th>{ __( 'Price', 'at-vehicle-configurator' ) }</th>
						</tr>
					</thead>
					<tbody>
						{ bundleData && (
							<tr className="configurator-summary-bundle-row">
								<td>
									<div className="configurator-summary-item">
										{ getImageUrl( bundleData ) && (
											<img
												src={ getImageUrl( bundleData ) }
												alt={ getImageAltText( bundleData, 'Bundle' ) }
												className="configurator-summary-item-image"
												loading="lazy"
												decoding="async"
											/>
										) }
										<span className="configurator-summary-item-title">
											{ decodeHtmlEntities( bundleData.title?.rendered ) || __( 'Bundle', 'at-vehicle-configurator' ) }
											<span className="configurator-summary-item-label"> ({ __( 'Bundle', 'at-vehicle-configurator' ) })</span>
										</span>
									</div>
								</td>
								<td>
									{ __( 'Pre-configured Bundle', 'at-vehicle-configurator' ) }
								</td>
								<td>
									<span className="configurator-summary-item-price">
										{ ( () => {
											const bundleProducts = bundleData.acf?.selected_optionals || [];
											if ( bundleProducts.length === 0 ) {
												return formatPrice( 0 );
											}
											let total = 0;
											bundleProducts.forEach( productId => {
												const product = productsData.find( p => p.id === productId );
												if ( product?.data ) {
													total += getLine( product.data, productId ).total;
												}
											} );
											return formatPrice( total );
										} )() }
									</span>
								</td>
							</tr>
						) }
						{ config.allowNoBaseModel && selectedBaseModel === 0 && (
							<tr className="configurator-summary-no-base-row">
								<td>
									<div className="configurator-summary-item">
										<span className="configurator-summary-item-title">
											{ __( 'No base model', 'at-vehicle-configurator' ) }
										</span>
									</div>
								</td>
								<td>{ __( 'N/A', 'at-vehicle-configurator' ) }</td>
								<td>
									<span className="configurator-summary-item-price">{ formatPrice( 0 ) }</span>
								</td>
							</tr>
						) }
						{ baseModelData && (
							<tr>
								<td>
									<div className="configurator-summary-item">
										{ getImageUrl( baseModelData ) && (
											<img
												src={ getImageUrl( baseModelData ) }
												alt={ getImageAltText( baseModelData, 'Base Model' ) }
												className="configurator-summary-item-image"
												loading="lazy"
												decoding="async"
											/>
										) }
										<span className="configurator-summary-item-title">
											{ decodeHtmlEntities( baseModelData.title?.rendered ) || __( 'Base Model', 'at-vehicle-configurator' ) }
										</span>
										<button
											type="button"
											className="configurator-summary-item-info-icon"
											onClick={ () => handleInfoClick( selectedBaseModel, true ) }
											aria-label={ `${ __( 'View information about', 'at-vehicle-configurator' ) } ${ decodeHtmlEntities( baseModelData.title?.rendered ) || __( 'base model', 'at-vehicle-configurator' ) }` }
										>
											<span className="screen-reader-text">{ __( 'Information', 'at-vehicle-configurator' ) }</span>
											i
										</button>
									</div>
								</td>
								<td>
									{ getSku( baseModelData ) ? (
										<>{ __( 'SKU:', 'at-vehicle-configurator' ) } { getSku( baseModelData ) }</>
									) : (
										<>{ __( 'Base Model ID:', 'at-vehicle-configurator' ) } { selectedBaseModel }</>
									) }
								</td>
								{ renderPriceCell( getLine( baseModelData, null ) ) }
							</tr>
						) }
						{ groupedProducts().map( ( group, groupIndex ) => {
							const groupCount = getGroupCount( group );
							return (
								<Fragment key={ groupIndex }>
									<tr className="configurator-summary-group-header">
										<td colSpan={ colCount }>
											<strong>
												{ group.groupName } ({ groupCount })
											</strong>
										</td>
									</tr>
									{ group.directProducts.map( ( { id, data } ) => {
										if ( ! data ) {
											return (
												<tr key={ id }>
													<td>{ __( 'Product', 'at-vehicle-configurator' ) }</td>
													<td>{ __( 'Product ID:', 'at-vehicle-configurator' ) } { id }</td>
													<td>-</td>
												</tr>
											);
										}

										const sku = getSku( data );
										const line = getLine( data, id );
										const imageUrl = getImageUrl( data );

										return (
											<tr key={ id }>
												<td>
													<div className="configurator-summary-item">
														{ imageUrl && (
															<img
																src={ imageUrl }
																alt={ getImageAltText( data, 'Product' ) }
																className="configurator-summary-item-image"
																loading="lazy"
																decoding="async"
															/>
														) }
														<span className="configurator-summary-item-title">
															{ decodeHtmlEntities( data.title?.rendered ) || __( 'Product', 'at-vehicle-configurator' ) }
														</span>
														{ renderQuantity( data, id ) }
														<button
															type="button"
															className="configurator-summary-item-info-icon"
															onClick={ () => handleInfoClick( id, true ) }
															aria-label={ `${ __( 'View information about', 'at-vehicle-configurator' ) } ${ decodeHtmlEntities( data.title?.rendered ) || __( 'product', 'at-vehicle-configurator' ) }` }
														>
															<span className="screen-reader-text">{ __( 'Information', 'at-vehicle-configurator' ) }</span>
															i
														</button>
													</div>
												</td>
												<td>{ sku ? <>SKU: { sku }</> : <>Product ID: { id }</> }</td>
												{ renderPriceCell( line ) }
											</tr>
										);
									} ) }
									{ Object.entries( group.subGroups ).map( ( [ subGroupName, subGroupProducts ], subGroupIndex ) => (
										<Fragment key={ `${ groupIndex }-${ subGroupIndex }` }>
											<tr className="configurator-summary-subgroup-header">
												<td colSpan={ colCount }>
													<em>
														{ subGroupName } ({ subGroupProducts.length })
													</em>
												</td>
											</tr>
											{ subGroupProducts.map( ( { id, data } ) => {
												if ( ! data ) {
													return (
														<tr key={ id }>
															<td>{ __( 'Product', 'at-vehicle-configurator' ) }</td>
															<td>{ __( 'Product ID:', 'at-vehicle-configurator' ) } { id }</td>
															<td>-</td>
														</tr>
													);
												}

												const sku = getSku( data );
												const line = getLine( data, id );
												const imageUrl = getImageUrl( data );

												return (
													<tr key={ id }>
														<td>
															<div className="configurator-summary-item">
																{ imageUrl && (
																	<img
																		src={ imageUrl }
																		alt={ decodeHtmlEntities( data.title?.rendered ) || 'Product' }
																		className="configurator-summary-item-image"
																		loading="lazy"
																		decoding="async"
																	/>
																) }
																<span className="configurator-summary-item-title">
																	{ decodeHtmlEntities( data.title?.rendered ) || __( 'Product', 'at-vehicle-configurator' ) }
																</span>
																{ renderQuantity( data, id ) }
																<button
																	type="button"
																	className="configurator-summary-item-info-icon"
																	onClick={ () => handleInfoClick( id, true ) }
																	aria-label={ `${ __( 'View information about', 'at-vehicle-configurator' ) } ${ decodeHtmlEntities( data.title?.rendered ) || __( 'product', 'at-vehicle-configurator' ) }` }
																>
																	<span className="screen-reader-text">{ __( 'Information', 'at-vehicle-configurator' ) }</span>
																	i
																</button>
															</div>
														</td>
														<td>{ sku ? <>{ __( 'SKU:', 'at-vehicle-configurator' ) } { sku }</> : <>{ __( 'Product ID:', 'at-vehicle-configurator' ) } { id }</> }</td>
														{ renderPriceCell( line ) }
													</tr>
												);
											} ) }
										</Fragment>
									) ) }
								</Fragment>
							);
						} 					) }
					</tbody>
				</table>
			</div>
			<ProductInfoModal
				productId={ infoModalProductId }
				isOpen={ infoModalIsOpen }
				onClose={ () => {
					setInfoModalIsOpen( false );
					setInfoModalProductId( null );
				} }
				isSelected={ infoModalIsSelected }
			/>
		</div>
	);
};

export default SummaryTable;
